import { ALL_CATEGORIES_FILTER } from '@/utils/category';
import { compareByCategoryName } from './use-catalog-variant-pack-groups';
import { FilteredCategoryItemGroup } from '@/contexts/item-context-types';
import { useCallback, useMemo } from 'react';
import { useSupplierDynamicConfigValue } from './use-dynamic-config-value';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { VariantPackGroupType } from '@/enums';

interface GenerationOptions {
  categoryUUIDsToFilterFrom: string[];
  excludeExtraCategories?: boolean;
  filterByCategoryType?: boolean;
  preserveSubcategoryUUIDs?: Set<string>;
  selectedCategoryUUID?: string;
  subcategoryItemCounts?: Map<string, number>;
}
export const useItemGroupCategoryFilters = ({
  categoryUUIDsToFilterFrom,
  excludeExtraCategories = true,
  filterByCategoryType = true,
  selectedCategoryUUID,
  subcategoryItemCounts,
  preserveSubcategoryUUIDs,
}: GenerationOptions) => {
  const { variantPackGroupMap } = useVariantPackInfoContext();
  const specialCharsLast = useSupplierDynamicConfigValue(
    'sort_categories_with_special_chars_last',
    false,
  );

  const uniqueUUIDSet = useMemo(() => {
    return new Set(categoryUUIDsToFilterFrom);
  }, [categoryUUIDsToFilterFrom]);

  const filteredItemGroupsBySelectedCategory = useMemo(() => {
    if (!selectedCategoryUUID) {
      return [];
    }
    return categoryUUIDsToFilterFrom.reduce<string[]>((acc, uuid) => {
      if (
        variantPackGroupMap[selectedCategoryUUID]?.directSubgroupUUIDs.includes(
          uuid,
        )
      ) {
        acc.push(uuid);
      }
      return acc;
    }, []);
  }, [categoryUUIDsToFilterFrom, variantPackGroupMap, selectedCategoryUUID]);

  const generateItemGroupCategories = useCallback(
    (
      uuids: string[],
      showAllFilter: boolean,
      parentUUID = '',
    ): FilteredCategoryItemGroup[] => {
      const result: FilteredCategoryItemGroup[] = [];

      for (const uuid of uuids) {
        if (excludeExtraCategories && !uniqueUUIDSet.has(uuid)) {
          continue;
        }
        const variantPackGroup = variantPackGroupMap[uuid];

        if (variantPackGroup) {
          const path = `${parentUUID}/${uuid}`;
          const directSubcategories =
            variantPackGroup.directSubgroupUUIDs &&
            variantPackGroup.directSubgroupUUIDs.length
              ? [
                  ...(showAllFilter
                    ? [{ name: ALL_CATEGORIES_FILTER, path, uuid }]
                    : []),
                  ...generateItemGroupCategories(
                    variantPackGroup.directSubgroupUUIDs || [],
                    showAllFilter,
                    path,
                  ),
                ]
              : [];

          // Filter empty subcategories if item counts are provided
          // BUT preserve selected subcategories (and their ancestors) even if they appear empty
          if (subcategoryItemCounts) {
            const isPreserved = preserveSubcategoryUUIDs?.has(uuid);

            const itemCount = subcategoryItemCounts.get(uuid) || 0;
            const hasItems = itemCount > 0;
            // Check filtered children - if all children are also empty, directSubcategories will be empty
            const hasNonEmptyChildren = directSubcategories.length > 0;

            // Filter out subcategories that have no items AND no non-empty children
            // UNLESS they are preserved (selected or ancestor of selected)
            if (!isPreserved && !hasItems && !hasNonEmptyChildren) {
              continue;
            }
          }

          result.push({
            directSubcategories:
              !showAllFilter || directSubcategories.length > 1
                ? directSubcategories
                : [],
            name: variantPackGroup.name,
            path,
            type: variantPackGroup.type,
            uuid,
          });
        }
      }

      return result.sort((a, b) =>
        compareByCategoryName(a, b, specialCharsLast),
      );
    },
    [
      excludeExtraCategories,
      uniqueUUIDSet,
      variantPackGroupMap,
      subcategoryItemCounts,
      preserveSubcategoryUUIDs,
      specialCharsLast,
    ],
  );

  const categoryItemGroupsInSelector = useMemo(() => {
    if (!categoryUUIDsToFilterFrom.length) {
      return [];
    }

    const categories = generateItemGroupCategories(
      selectedCategoryUUID
        ? filteredItemGroupsBySelectedCategory
        : categoryUUIDsToFilterFrom,
      true,
    );

    return [
      { name: ALL_CATEGORIES_FILTER, path: '', uuid: null },
      ...(filterByCategoryType
        ? categories.filter(
            (group) => group.type === VariantPackGroupType.Category,
          )
        : categories),
    ];
  }, [
    categoryUUIDsToFilterFrom,
    filterByCategoryType,
    filteredItemGroupsBySelectedCategory,
    generateItemGroupCategories,
    selectedCategoryUUID,
  ]);

  const categoryItemGroupsInSelectorWithoutAllFilter = useMemo(() => {
    if (!categoryUUIDsToFilterFrom.length) {
      return [];
    }

    const categories = generateItemGroupCategories(
      selectedCategoryUUID
        ? filteredItemGroupsBySelectedCategory
        : categoryUUIDsToFilterFrom,
      false,
      selectedCategoryUUID && `/${selectedCategoryUUID}`,
    );

    return [
      // show All filter only for the top level
      { name: ALL_CATEGORIES_FILTER, path: '', uuid: null },
      ...(filterByCategoryType
        ? categories.filter(
            (group) => group.type === VariantPackGroupType.Category,
          )
        : categories),
    ];
  }, [
    categoryUUIDsToFilterFrom,
    filterByCategoryType,
    filteredItemGroupsBySelectedCategory,
    generateItemGroupCategories,
    selectedCategoryUUID,
  ]);

  return {
    categoryItemGroupsInSelector,
    categoryItemGroupsInSelectorWithoutAllFilter,
  };
};
